import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkImage, {
  withLazyLoading,
  withLoadingBehavior,
} from '@skyscanner-internal/backpack-web/bpk-component-image';
import BpkPrice, {
  SIZES,
  ALIGNS,
} from '@skyscanner-internal/backpack-web/bpk-component-price';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import { useMBDContext } from '../../common/MBD/useMBDContext';

import type { PopularDestination } from '@skyscanner-internal/landing-pages-shared-types/types-shared/PopularDestinationsProps';

import STYLES from './DestinationContainer.module.scss';

const IMAGE_WIDTH = 392;
const IMAGE_HEIGHT = 184;
const FALLBACK_IMAGE_URL =
  'https://content.skyscnr.com/m/2141e25d48558df4/original/fillter-illu_NB.svg?resize=500px:500px&quality=90';
const documentIfExists = typeof window !== 'undefined' ? document : null;
const imageParams = '?crop=960px:640px&quality=60';
const LazyLoadedImage = withLoadingBehavior(
  withLazyLoading(BpkImage, documentIfExists),
);

type DestinationContainerProps = {
  destination: PopularDestination;
  from: string;
  perDay: string;
  index: number;
  componentName?: string;
  relFollow?: boolean;
  mobileScroll?: boolean;
  noShowImage?: boolean;
};

const DestinationContainer = ({
  componentName = undefined,
  destination,
  from,
  index,
  mobileScroll = false,
  noShowImage = false,
  perDay,
  relFollow = false,
}: DestinationContainerProps) => {
  const imageUrl =
    destination.alternativeImageUrl ||
    destination.imageUrl ||
    FALLBACK_IMAGE_URL;
  const { buildMBDParam, rawComponentName } = useMBDContext();

  const mbdName = `${componentName || rawComponentName}_item`;
  const mbdProps = {
    image_name: imageUrl.split('/').pop() || '',
  };

  return (
    <BpkCard
      className={
        mobileScroll
          ? STYLES['DestinationContainer__card-with-width']
          : STYLES.DestinationContainer__card
      }
      padded={false}
      href={destination.redirectUrl}
      rel={relFollow ? '' : 'nofollow'}
      role="link"
      data-tracking-element-id={`${mbdName}_${index}`}
      data-tracking-common-params={buildMBDParam(mbdName, mbdProps)}
    >
      {!noShowImage && (
        <div className={STYLES.DestinationContainer__image}>
          <LazyLoadedImage
            src={imageUrl + imageParams}
            altText=""
            aspectRatio={IMAGE_WIDTH / IMAGE_HEIGHT}
          />
        </div>
      )}
      <div className={STYLES.DestinationContainer__content}>
        <div className={STYLES.DestinationContainer__leftSection}>
          <BpkText tagName="h3" textStyle={TEXT_STYLES.heading4}>
            {destination.subtitle}
          </BpkText>
          {destination.mostPopularCarType && (
            <BpkText
              tagName="p"
              color={TEXT_COLORS.textSecondary}
              textStyle={TEXT_STYLES.caption}
              dangerouslySetInnerHTML={{
                __html: destination.mostPopularCarType,
              }}
            />
          )}
          {destination.distance && (
            <BpkText
              tagName="p"
              color={TEXT_COLORS.textSecondary}
              textStyle={TEXT_STYLES.caption}
            >
              {destination.distance}
            </BpkText>
          )}
        </div>
        {destination.cheapestPrice && (
          <div className={STYLES.DestinationContainer__rightSection}>
            <BpkPrice
              size={SIZES.small}
              align={ALIGNS.right}
              leadingText={from}
              price={destination.cheapestPrice}
              trailingText={perDay}
            />
          </div>
        )}
      </div>
    </BpkCard>
  );
};

export default DestinationContainer;
